import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import './AdminComponents.css';

interface StrategyConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategyId: number | null;
  strategyName?: string;
}

interface StrategyConfig {
  fixedOrderAmount?: number;
  leverage?: number;
  trailingStopLoss?: number;
  takeProfit?: number;
  basketTakeProfit?: number;
  exchange?: string;
  selectedCoins?: string[];
  coinSelectionMode?: 'TOP_N' | 'MANUAL' | 'ALL_COINS';
  coinLimit?: number;
  stopLossType?: 'CLASSIC' | 'TRAILING';
  longTrailingStopLoss?: number;
  shortTrailingStopLoss?: number;
  enableStopLossPerCoin?: boolean;
  enableTakeProfitPerCoin?: boolean;
  enableBasketTakeProfit?: boolean;
  marketType?: 'SPOT' | 'FUTURES';
  maxOpenPositions?: number;
  paperTrading?: boolean;
  enableVolatilityFilter?: boolean;
  minMarketVolatility?: number;
  maxMarketVolatility?: number;
  volatilityCalculationCoins?: number;
  volatilityCalculationMethod?: 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO';
  marginMode?: 'CROSS' | 'ISOLATED';
  orderType?: 'MARKET' | 'LIMIT';
  limitOffset?: number;
  limitTimeoutSeconds?: number;
  minMarketActivityThreshold?: number;
  enableMicroTsl?: boolean;
  microTslActivationPercent?: number;
  microTslDistancePercent?: number;
  minProfitExitPercent?: number;
}

interface TradingStrategy {
  id: number;
  name: string;
  strategyType: string;
  status: string;
  config: string;
  createdAt: string;
  updatedAt: string;
}

const StrategyConfigModal: React.FC<StrategyConfigModalProps> = ({ isOpen, onClose, strategyId, strategyName: propStrategyName }) => {
  const adminApiService = useAdminApiService();
  const [strategy, setStrategy] = useState<TradingStrategy | null>(null);
  const [config, setConfig] = useState<StrategyConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && strategyId) {
      loadStrategyConfig();
    } else {
      setStrategy(null);
      setConfig(null);
      setError(null);
    }
  }, [isOpen, strategyId]);

  const loadStrategyConfig = async () => {
    if (!strategyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminApiService.getStrategyDetails(strategyId);
      if (response.success && response.data) {
        setStrategy(response.data);
        try {
          const configData = typeof response.data.config === 'string' 
            ? JSON.parse(response.data.config) 
            : response.data.config;
          setConfig(configData);
        } catch (e) {
          console.error('Failed to parse config:', e);
          setError('Failed to parse strategy configuration');
        }
      } else {
        setError(response.error || 'Failed to load strategy configuration');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading strategy configuration');
      console.error('Error loading strategy config:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') {
      if (value < 1 && value > 0) {
        return value.toFixed(4);
      }
      return value.toString();
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None';
    }
    return String(value);
  };

  if (!isOpen) return null;

  const isMobile = window.innerWidth <= 768;
  const displayName = propStrategyName || strategy?.name || 'Strategy';

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10001 }}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? '95vw' : '700px',
          maxWidth: isMobile ? '95vw' : '700px',
          maxHeight: isMobile ? '90vh' : '85vh',
          margin: isMobile ? '10px auto' : 'auto',
          borderRadius: isMobile ? '8px' : '12px',
          padding: '0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1a202c' }}>
            Strategy Configuration: {displayName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#6b7280',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '16px', color: '#666' }}>Loading configuration...</div>
            </div>
          )}

          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#991b1b',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && strategy && config && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Basic Info */}
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Basic Information
                </h3>
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <table style={{ width: '100%', fontSize: '12px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280', width: '40%' }}>Strategy ID:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{strategy.id}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Name:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{strategy.name}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Type:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{strategy.strategyType.replace('_', ' ')}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Status:</td>
                        <td style={{ padding: '6px' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: strategy.status === 'ACTIVE' ? '#d1fae5' : strategy.status === 'PAUSED' ? '#fef3c7' : '#fee2e2',
                            color: strategy.status === 'ACTIVE' ? '#065f46' : strategy.status === 'PAUSED' ? '#92400e' : '#991b1b'
                          }}>
                            {strategy.status}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Created:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>
                          {new Date(strategy.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Trading Configuration */}
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Trading Configuration
                </h3>
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <table style={{ width: '100%', fontSize: '12px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280', width: '40%' }}>Order Amount:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>${formatValue(config.fixedOrderAmount)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Leverage:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.leverage)}x</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Market Type:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.marketType)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Exchange:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.exchange)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Max Positions:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.maxOpenPositions)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Order Type:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.orderType)}</td>
                      </tr>
                      {config.orderType === 'LIMIT' && (
                        <>
                          <tr>
                            <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Limit Offset:</td>
                            <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.limitOffset)}%</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Limit Timeout:</td>
                            <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.limitTimeoutSeconds)}s</td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Margin Mode:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.marginMode)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Paper Trading:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.paperTrading)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Risk Management */}
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Risk Management
                </h3>
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <table style={{ width: '100%', fontSize: '12px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280', width: '40%' }}>Stop Loss Type:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.stopLossType)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Trailing Stop Loss:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.trailingStopLoss)}%</td>
                      </tr>
                      {config.longTrailingStopLoss && (
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>🔵 Long SL:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{config.longTrailingStopLoss}%</td>
                        </tr>
                      )}
                      {config.shortTrailingStopLoss && (
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>🔴 Short SL:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{config.shortTrailingStopLoss}%</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Take Profit:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.takeProfit)}%</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Basket Take Profit:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.basketTakeProfit)}%</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Stop Loss Per Coin:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.enableStopLossPerCoin)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Take Profit Per Coin:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.enableTakeProfitPerCoin)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Basket Take Profit Enabled:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.enableBasketTakeProfit)}</td>
                      </tr>
                      {config.enableMicroTsl && (
                        <>
                          <tr>
                            <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Micro TSL Enabled:</td>
                            <td style={{ padding: '6px', color: '#1a202c' }}>Yes</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Micro TSL Activation:</td>
                            <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.microTslActivationPercent)}%</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Micro TSL Distance:</td>
                            <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.microTslDistancePercent)}%</td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>🛡️ Min Profit Exit:</td>
                        <td style={{ padding: '6px', color: config.minProfitExitPercent != null && config.minProfitExitPercent > 0 ? '#065f46' : '#9ca3af' }}>
                          {config.minProfitExitPercent != null && config.minProfitExitPercent > 0
                            ? `${config.minProfitExitPercent}% (TSL blocked below this)`
                            : 'Disabled'
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Coin Selection */}
              <div>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Coin Selection
                </h3>
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}>
                  <table style={{ width: '100%', fontSize: '12px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280', width: '40%' }}>Selection Mode:</td>
                        <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.coinSelectionMode)}</td>
                      </tr>
                      {config.coinSelectionMode === 'TOP_N' && (
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Coin Limit:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.coinLimit)}</td>
                        </tr>
                      )}
                      {config.selectedCoins && config.selectedCoins.length > 0 && (
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280', verticalAlign: 'top' }}>Selected Coins:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {config.selectedCoins.map((coin, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    backgroundColor: '#eef2ff',
                                    color: '#3730a3'
                                  }}
                                >
                                  {coin}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      {config.minMarketActivityThreshold && (
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Market Activity Threshold:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.minMarketActivityThreshold)}%</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Volatility Filter */}
              {config.enableVolatilityFilter && (
                <div>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                    Volatility Filter
                  </h3>
                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280', width: '40%' }}>Min Volatility:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.minMarketVolatility)}%</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Max Volatility:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.maxMarketVolatility)}%</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Calculation Coins:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.volatilityCalculationCoins)}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '6px', fontWeight: 600, color: '#6b7280' }}>Calculation Method:</td>
                          <td style={{ padding: '6px', color: '#1a202c' }}>{formatValue(config.volatilityCalculationMethod)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyConfigModal;
